import { useEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, Pause, Play, Repeat, SkipBack, SkipForward, Sparkles, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useScrollReveal } from '@/lib/useScrollReveal';
import { getStudioImage } from '@/lib/studioAssets';
import { getCgSaga, getDahuangVoice } from '@/lib/dahuangAssets';
import {
  dahuangCharacters,
  dahuangDeveloperNote,
  dahuangFeatures,
  dahuangHero,
  dahuangLinks,
  dahuangSaga,
  dahuangSoundtrack,
  dahuangStory,
} from '@/lib/dahuangConfig';

// ---------------------------------------------------------------- 玩法详解 模态
function GameplayLightbox({ feature, onClose }: { feature: (typeof dahuangFeatures)[number]; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div className="dh-lightbox" role="dialog" aria-modal="true" aria-label={feature.title} onClick={onClose}>
      <div className="dh-lightbox__panel" onClick={(e) => e.stopPropagation()}>
        <button className="dh-lightbox__close" onClick={onClose} aria-label="关闭">
          <X size={20} />
        </button>
        <div className="dh-lightbox__media" style={{ backgroundImage: `url(${feature.image})` }}>
          <span className="dh-media-tag">PREVIEW</span>
        </div>
        <div className="dh-lightbox__body">
          <div className="dh-kicker">
            <span className="dh-kicker__icon">{feature.icon}</span>
            <span>{feature.subtitle}</span>
          </div>
          <h3 className="dh-lightbox__title">{feature.title}</h3>
          <p className="dh-lightbox__desc">{feature.description}</p>
          <div className="dh-lightbox__cols">
            <div>
              <p className="dh-col-h">玩法机制</p>
              <p className="dh-col-t">{feature.mechanic}</p>
            </div>
            <div>
              <p className="dh-col-h">剧情钩子</p>
              <p className="dh-col-t">{feature.storyHook}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- NPC 浮空面板
function CharacterPanel({ character, onClose }: { character: (typeof dahuangCharacters)[number]; onClose: () => void }) {
  const [playing, setPlaying] = useState(false);
  const [audio, setAudio] = useState<HTMLAudioElement | null>(null);
  const voiceUrl = character.voice ? getDahuangVoice(character.voice) : '';

  useEffect(() => {
    return () => {
      if (audio) {
        audio.pause();
        audio.src = '';
      }
    };
  }, [audio]);

  const toggleVoice = () => {
    if (!voiceUrl) return;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
      setAudio(null);
      setPlaying(false);
      return;
    }
    const a = new Audio(voiceUrl);
    a.play().catch(() => setPlaying(false));
    a.addEventListener('ended', () => setPlaying(false));
    setAudio(a);
    setPlaying(true);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="dh-charpanel" role="dialog" aria-modal="true" aria-label={character.name} onClick={onClose}>
      <button className="dh-charpanel__close" onClick={onClose} aria-label="关闭">
        <X size={20} />
      </button>
      <div className="dh-charpanel__panel" onClick={(e) => e.stopPropagation()}>
        <div className="dh-charpanel__media">
          <img src={character.image} alt={character.name} />
        </div>
        <div className="dh-charpanel__body">
          <span className="dh-charpanel__camp">{character.camp}</span>
          <h3 className="dh-charpanel__name">{character.name}</h3>
          <p className="dh-charpanel__role">{character.role}</p>
          <p className="dh-charpanel__desc">{character.fullBio || character.description}</p>
          <div className="dh-charpanel__actions">
            {voiceUrl ? (
              <button className={`dh-voice-btn ${playing ? 'is-playing' : ''}`} onClick={toggleVoice} aria-pressed={playing}>
                <span className="dh-voice-btn__wave" />
                {playing ? '播放中…' : '听 TA 说'}
              </button>
            ) : (
              <span className="dh-voice-btn dh-voice-btn--muted">配音筹备中</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- 剧情串讲
function SagaSection() {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const items = Array.from(document.querySelectorAll('.dh-saga__item'));
    if (items.length === 0 || typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = Number((entry.target as HTMLElement).dataset.index);
            if (!Number.isNaN(idx)) setActiveIndex(idx);
          }
        });
      },
      { threshold: 0.55, rootMargin: '-20% 0px -30% 0px' },
    );

    items.forEach((item, i) => {
      const el = item as HTMLElement;
      el.dataset.index = String(i);
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <section className="s-section" id="saga">
      <div className="s-reveal">
        <p className="s-eyebrow">THE SAGA</p>
        <h2 className="s-h2">七幕名场面 · 一场饥荒建国史</h2>
        <p className="s-lead">
          竖屏长卷，串起前期主线里最滚烫的七段对白。从破屋里一根削尖的木棍，到庄园墙上最后一缕炊烟 —— 这是理科生写给饥荒的答卷。
        </p>
      </div>
      <div className="dh-saga">
        <div className="dh-saga__media">
          {dahuangSaga.map((beat, i) => (
            <img
              key={beat.cgKey}
              src={getCgSaga(beat.cgKey)}
              alt={beat.title}
              loading="lazy"
              className={i === activeIndex ? 'is-visible' : ''}
            />
          ))}
        </div>
        <ol className="dh-saga__list">
          {dahuangSaga.map((beat) => (
            <li key={beat.cgKey} className="dh-saga__item s-reveal">
              <span className="dh-saga__index">{beat.index}</span>
              <div>
                <h3 className="dh-saga__title">{beat.title}</h3>
                <p className="dh-saga__excerpt">{beat.excerpt}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------- 古风「十二律」悬浮音乐播放器
// 仿末世房车官网「车载音乐」播放器技术（<audio> + React state），改走「古卷题款」国风。
// 支持：播放/暂停、上一曲/下一曲、单曲循环🔂 / 顺序循环🔁 切换、可拖动进度条。
function HuángYīnPlayer() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loopMode, setLoopMode] = useState<'single' | 'list'>('list');
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const tracks = dahuangSoundtrack;
  const currentTrack = tracks[currentIndex];

  useEffect(() => {
    setProgress(0);
    setDuration(0);
  }, [currentTrack.file]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (!isPlaying) {
      audio.pause();
      return;
    }
    const playPromise = audio.play();
    if (playPromise) playPromise.catch(() => setIsPlaying(false));
  }, [currentTrack.file, isPlaying]);

  const handleTimeUpdate = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.duration) {
      setDuration(audio.duration);
      setProgress((audio.currentTime / audio.duration) * 100);
    }
  };

  const selectTrack = (delta: number) => {
    setCurrentIndex((prev) => (prev + delta + tracks.length) % tracks.length);
  };

  const handleEnded = () => {
    if (loopMode === 'single') {
      const audio = audioRef.current;
      if (audio) {
        audio.currentTime = 0;
        audio.play().catch(() => setIsPlaying(false));
      }
    } else {
      selectTrack(1);
    }
  };

  const seek = (clientX: number, rect: DOMRect) => {
    const audio = audioRef.current;
    if (!audio || !audio.duration) return;
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    audio.currentTime = ratio * audio.duration;
    setProgress(ratio * 100);
  };

  const handleProgressPointer = (e: React.PointerEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    seek(e.clientX, rect);
  };

  const fmt = (s: number) => {
    if (!s || !isFinite(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  return (
    <div className="dh-music" role="region" aria-label="大荒十二律 · 古风音乐">
      <audio
        ref={audioRef}
        src={currentTrack.file}
        preload="metadata"
        crossOrigin="anonymous"
        onEnded={handleEnded}
        onLoadedMetadata={handleTimeUpdate}
        onTimeUpdate={handleTimeUpdate}
      />
      <div className="dh-music__cover" data-playing={isPlaying ? 'true' : 'false'}>
        <img src={currentTrack.cover} alt="" />
        <span className="dh-music__cover-seal">律</span>
      </div>

      <div className="dh-music__body">
        <div className="dh-music__title">
          <span className="dh-music__title-zh">{currentTrack.title}</span>
          <span className="dh-music__title-note">{currentTrack.note}</span>
        </div>
        <div className="dh-music__meta">
          <span>第 {currentIndex + 1} 律 / {tracks.length} 律</span>
          <span className="dh-music__time">{fmt(duration * progress / 100)} / {fmt(duration)}</span>
        </div>
        <div
          className="dh-music__progress"
          onClick={handleProgressPointer}
          onPointerDown={handleProgressPointer}
          role="slider"
          aria-label="播放进度"
          aria-valuenow={Math.round(progress)}
          aria-valuemin={0}
          aria-valuemax={100}
          tabIndex={0}
        >
          <span className="dh-music__progress-track" />
          <span className="dh-music__progress-fill" style={{ width: `${progress}%` }} />
          <span className="dh-music__progress-knob" style={{ left: `${progress}%` }} />
        </div>
      </div>

      <div className="dh-music__transport">
        <button type="button" aria-label="上一曲" className="dh-music__btn" onClick={() => selectTrack(-1)}>
          <SkipBack size={15} />
        </button>
        <button
          type="button"
          aria-label={isPlaying ? '暂停' : '播放'}
          className="dh-music__btn dh-music__btn--play"
          onClick={() => setIsPlaying((p) => !p)}
        >
          {isPlaying ? <Pause size={17} /> : <Play size={17} />}
        </button>
        <button type="button" aria-label="下一曲" className="dh-music__btn" onClick={() => selectTrack(1)}>
          <SkipForward size={15} />
        </button>
        <button
          type="button"
          aria-label={loopMode === 'single' ? '单曲循环' : '顺序循环'}
          aria-pressed={loopMode === 'single'}
          className="dh-music__btn dh-music__btn--loop"
          onClick={() => setLoopMode((m) => (m === 'single' ? 'list' : 'single'))}
        >
          <Repeat size={14} />
          {loopMode === 'single' && <sub className="dh-music__loop-one">1</sub>}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- 主页面
export default function DahuangSite() {
  useScrollReveal();

  const [activeFeatureId, setActiveFeatureId] = useState<string | null>(null);
  const [activeCharacterId, setActiveCharacterId] = useState<string | null>(null);

  const activeFeature = dahuangFeatures.find((f) => f.id === activeFeatureId) ?? null;
  const activeCharacter = dahuangCharacters.find((c) => c.id === activeCharacterId) ?? null;

  return (
    <main className="dahuang-shell">
      <header className="s-nav">
        <Link className="s-nav__brand" to="/">
          <img src={getStudioImage('logo')} alt="" />
          光年切片工作室
        </Link>
        <nav className="s-nav__links">
          <a href="#features">玩法</a>
          <a href="#characters">角色</a>
          <a href="#saga">剧情</a>
          <a href="#story">主线</a>
          <Link to="/rebirth-hoarder">末世房车</Link>
        </nav>
        <Link className="s-nav__cta" to="/">
          返回工作室
        </Link>
      </header>

      <section className="dh-hero">
        <div className="dh-hero__bg" style={{ backgroundImage: `url(${dahuangHero.background})` }} />
        <div className="dh-hero__inner">
          <div className="dh-hero__copy">
            <p className="s-eyebrow">{dahuangHero.eyebrow}</p>
            <h1 className="dh-hero__title">{dahuangHero.title}</h1>
            <p className="dh-hero__title-full">《{dahuangHero.titleFull}》</p>
            <div className="dh-hero__status">
              <strong>{dahuangHero.status}</strong>
              <span>{dahuangHero.statusNote}</span>
            </div>
            <p className="dh-hero__subtitle">{dahuangHero.subtitle}</p>
            <p className="dh-hero__desc">{dahuangHero.description}</p>
            <div className="dh-hero__actions">
              <span className="s-btn s-btn--disabled">TapTap · 敬请期待</span>
              <span className="s-btn s-btn--disabled">抖音小游戏 · 敬请期待</span>
            </div>
            <div className="dh-badges">
              {dahuangHero.badges.map((badge) => (
                <span key={badge}>{badge}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="s-section" id="features">
        <div className="s-reveal">
          <p className="s-eyebrow">CORE GAMEPLAY</p>
          <h2 className="s-h2">现代知识的古代妙用</h2>
          <p className="s-lead">
            不玩虚的。大到用化学公式配化肥，小到用信息差搞期票做空，每一个系统都在具备游戏性的同时，有着符合逻辑的现实映射。
          </p>
        </div>
        <div className="dh-features">
          {dahuangFeatures.map((feature) => (
            <button
              key={feature.id}
              type="button"
              className="dh-feature s-reveal"
              onClick={() => setActiveFeatureId(feature.id)}
              aria-label={`查看 ${feature.title} 详细介绍`}
            >
              <div className="dh-feature__bg" style={{ backgroundImage: `url(${feature.image})` }} />
              <div className="dh-feature__icon">{feature.icon}</div>
              <h3 className="dh-feature__title">{feature.title}</h3>
              <p className="dh-feature__subtitle">{feature.subtitle}</p>
              <p className="dh-feature__desc">{feature.description}</p>
              <span className="dh-feature__cta">点击查看详情</span>
            </button>
          ))}
        </div>
      </section>

      <section className="s-section" id="characters">
        <div className="s-reveal">
          <p className="s-eyebrow">CAST</p>
          <h2 className="s-h2">与你并肩的人</h2>
          <p className="s-lead">从死心落泪到炽热崇拜，这片赤地上的每一段羁绊，都是逆袭路上最真实的驱动力。点下角色卡，听一听他们。</p>
        </div>
        <div className="dh-chars">
          {dahuangCharacters.map((character) => (
            <button
              key={character.id}
              type="button"
              className="dh-char s-reveal"
              onClick={() => setActiveCharacterId(character.id)}
              aria-label={`查看 ${character.name}`}
            >
              <div className="dh-char__media">
                <img src={character.image} alt={character.name} loading="lazy" />
              </div>
              <div className="dh-char__body">
                <span className="dh-char__camp">{character.camp}</span>
                <h3 className="dh-char__name">{character.name}</h3>
                <p className="dh-char__role">{character.role}</p>
                <p className="dh-char__desc">{character.description}</p>
              </div>
            </button>
          ))}
        </div>
      </section>

      <SagaSection />

      <section className="s-section" id="story">
        <div className="s-reveal">
          <p className="s-eyebrow">{dahuangStory.eyebrow}</p>
          <h2 className="s-h2">{dahuangStory.title}</h2>
          <p className="s-lead">{dahuangStory.description}</p>
        </div>
        <div className="dh-timeline">
          {dahuangStory.chapters.map((chapter) => (
            <article key={chapter.index} className="dh-chapter s-reveal">
              <span className="dh-chapter__index">{chapter.index}</span>
              <div>
                <h3 className="dh-chapter__title">{chapter.title}</h3>
                <p className="dh-chapter__summary">{chapter.summary}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="dh-note">
        <div className="dh-note__inner s-reveal">
          <p className="s-eyebrow">{dahuangDeveloperNote.eyebrow}</p>
          <h2 className="s-h2">{dahuangDeveloperNote.title}</h2>
          <div className="dh-note__body">
            {dahuangDeveloperNote.paragraphs.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>
          <p className="dh-note__sign">{dahuangDeveloperNote.signature}</p>
        </div>
      </section>

      <div className="dh-crosslink">
        <div className="dh-crosslink__card s-reveal">
          <div className="dh-crosslink__bg" style={{ backgroundImage: `url(${getStudioImage('rv_hero')})` }} />
          <div className="dh-crosslink__copy">
            <p className="s-eyebrow" style={{ marginBottom: 10 }}>
              <Sparkles size={13} style={{ display: 'inline', verticalAlign: '-1px', marginRight: 4 }} />
              同一个工作室的另一部作品
            </p>
            <h3>末世：我有一辆房车</h3>
            <p>如果你曾在房车末世里和尸潮搏斗过，那么这一次，欢迎来到这个大饥荒时代。</p>
          </div>
          <Link className="s-btn s-btn--primary" to="/rebirth-hoarder">
            前往房车官网
            <ArrowRight size={17} />
          </Link>
        </div>
      </div>

      <footer className="s-footer">
        <div className="s-footer__inner">
          <Link className="s-link" to="/">
            <ArrowLeft size={15} />
            返回光年切片工作室
          </Link>
          <span>《回到大荒》 · 预上线 · 敬请期待</span>
        </div>
        <div className="s-footer__legal">
          <a href={dahuangLinks.userAgreement}>用户协议</a>
          <a href={dahuangLinks.privacyPolicy}>隐私政策</a>
        </div>
      </footer>

      {activeFeature && <GameplayLightbox feature={activeFeature} onClose={() => setActiveFeatureId(null)} />}
      {activeCharacter && <CharacterPanel character={activeCharacter} onClose={() => setActiveCharacterId(null)} />}
      <HuángYīnPlayer />
    </main>
  );
}
